import { addDays, differenceInCalendarDays, isSameDay, startOfDay } from 'date-fns'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'

export type UpcomingDay = {
  /** ISO date, e.g. "2026-09-29" — also the drop-target section id. */
  key: string
  date: Date
  tasks: Array<TaskDocument>
}

export type UpcomingGroups = {
  overdue: Array<TaskDocument>
  /** Always 7 entries, tomorrow first; empty days stay (design 34a). */
  days: Array<UpcomingDay>
  /** Ranges that started by the last day but are due after it. */
  later: Array<TaskDocument>
}

export const OVERDUE_SECTION = 'overdue'
export const LATER_SECTION = 'later'

export const dayKey = (value: Date | number) => {
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Bucket Next 7 days (design 34a): overdue on top, then every one of the
 * next 7 days — empty ones included, so free days are visible and take drops —
 * then ranges running past the window. Order inside a bucket is preserved.
 */
export function groupUpcoming(tasks: ReadonlyArray<TaskDocument>, now: Date = new Date()): UpcomingGroups {
  const today = startOfDay(now)
  const days: Array<UpcomingDay> = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(today, i + 1)
    return { key: dayKey(date), date, tasks: [] }
  })
  const byKey = new Map(days.map((day) => [day.key, day]))
  const overdue: Array<TaskDocument> = []
  const later: Array<TaskDocument> = []

  for (const task of tasks) {
    if (!task.dueDate) continue
    if (task.dueDate < today.getTime()) {
      overdue.push(task)
      continue
    }
    const day = byKey.get(dayKey(task.dueDate))
    if (day) day.tasks.push(task)
    // Due today belongs to Today; only what ends after the last day is "later".
    else if (task.dueDate >= addDays(today, 8).getTime()) later.push(task)
  }
  return { overdue, days, later }
}

/** Which section a task sits in, for cross-section drags. */
export function upcomingSectionOf(task: TaskDocument, groups: UpcomingGroups): string | undefined {
  if (groups.overdue.some((t) => t.id === task.id)) return OVERDUE_SECTION
  if (groups.later.some((t) => t.id === task.id)) return LATER_SECTION
  return groups.days.find((day) => day.tasks.some((t) => t.id === task.id))?.key
}

/**
 * Whether the due date carries a time someone chose. 11:59 PM is the default
 * end of day, not a time (design 34a); legacy rows without a type count only
 * when the time is neither midnight nor end of day.
 */
export function hasChosenTime(task: Pick<TaskDocument, 'dueDate' | 'dueDateType'>): boolean {
  if (!task.dueDate) return false
  if (task.dueDateType) return task.dueDateType === 'datetime'
  const date = new Date(task.dueDate)
  const minutes = date.getHours() * 60 + date.getMinutes()
  return minutes !== 0 && minutes !== 23 * 60 + 59
}

/** "Mon" within a week of today, otherwise "Sep 20". */
export function shortDayLabel(
  date: Date | number,
  fmt: { weekdayShort: (d: Date | number) => string; monthDay: (d: Date | number) => string },
  now: Date = new Date(),
): string {
  return Math.abs(differenceInCalendarDays(date, now)) < 7 ? fmt.weekdayShort(date) : fmt.monthDay(date)
}

/** A range whose start falls on an earlier day than its due date. */
export function isRange(task: Pick<TaskDocument, 'startDate' | 'dueDate'>): boolean {
  return !!task.startDate && !!task.dueDate && !isSameDay(task.startDate, task.dueDate) && task.startDate < task.dueDate
}

export type WeekSpan = {
  task: TaskDocument
  /** First and last day column it covers, 0–6. */
  from: number
  to: number
  /** Row inside the span band; spans that overlap never share one. */
  lane: number
  /** Started before tomorrow / due after the last day: that end is cut off. */
  clippedStart: boolean
  clippedEnd: boolean
}

export type WeekLayout = {
  spans: Array<WeekSpan>
  /** Per day column, how many lanes pass over it (its cards sit below them). */
  lanesByDay: Array<number>
  /** Each day's cards, without the ranges drawn as spans. */
  cardsByDay: Array<Array<TaskDocument>>
}

/**
 * Week layout (design 34b): a range that's under way this week becomes a bar
 * across its days instead of a card on its due day, including ones running
 * past the window. Overlapping bars stack into lanes, earliest first.
 */
export function layoutWeek(groups: UpcomingGroups): WeekLayout {
  const first = groups.days[0]
  const count = groups.days.length
  if (!first || count === 0) return { spans: [], lanesByDay: [], cardsByDay: [] }
  const indexOf = (value: number) => differenceInCalendarDays(value, first.date)

  const candidates = [...groups.days.flatMap((day) => day.tasks), ...groups.later].filter(isRange)
  const placed = candidates
    .map((task) => {
      const start = indexOf(task.startDate!)
      const end = indexOf(task.dueDate!)
      return {
        task,
        from: Math.max(0, start),
        to: Math.min(count - 1, end),
        clippedStart: start < 0,
        clippedEnd: end > count - 1,
      }
    })
    .sort((a, b) => a.from - b.from || b.to - a.to)

  const laneEnds: Array<number> = []
  const spans: Array<WeekSpan> = placed.map((span) => {
    let lane = laneEnds.findIndex((end) => end < span.from)
    if (lane === -1) lane = laneEnds.length
    laneEnds[lane] = span.to
    return { ...span, lane }
  })

  const lanesByDay = groups.days.map((_, i) =>
    spans.reduce((max, span) => (span.from <= i && i <= span.to ? Math.max(max, span.lane + 1) : max), 0),
  )
  const spanIds = new Set(spans.map((span) => span.task.id))
  const cardsByDay = groups.days.map((day) => day.tasks.filter((task) => !spanIds.has(task.id)))
  return { spans, lanesByDay, cardsByDay }
}
